/**
 * https://github.com/gre/bezier-easing
 * BezierEasing - use bezier curve for transition easing function
 * by Gaëtan Renaudeau 2014 - 2015 – MIT License
 */

import { EasingFunction } from "./Easing";

// These values are established by empiricism with tests (tradeoff: performance VS precision)
const NEWTON_ITERATIONS = 4;
const NEWTON_MIN_SLOPE = 0.001;
const SUBDIVISION_PRECISION = 0.0000001;
const SUBDIVISION_MAX_ITERATIONS = 10;

const kSplineTableSize = 11;
const kSampleStepSize = 1.0 / (kSplineTableSize - 1.0);

const float32ArraySupported = typeof Float32Array === "function";

function A(aA1: number, aA2: number) {
  return 1.0 - 3.0 * aA2 + 3.0 * aA1;
}
function B(aA1: number, aA2: number) {
  return 3.0 * aA2 - 6.0 * aA1;
}
function C(aA1: number) {
  return 3.0 * aA1;
}

// Returns x(t) given t, x1, and x2, or y(t) given t, y1, and y2.
function calcBezier(aT: number, aA1: number, aA2: number) {
  return ((A(aA1, aA2) * aT + B(aA1, aA2)) * aT + C(aA1)) * aT;
}

// Returns dx/dt given t, x1, and x2, or dy/dt given t, y1, and y2.
function getSlope(aT: number, aA1: number, aA2: number) {
  return 3.0 * A(aA1, aA2) * aT * aT + 2.0 * B(aA1, aA2) * aT + C(aA1);
}

function binarySubdivide(
  aX: number,
  aA: number,
  aB: number,
  mX1: number,
  mX2: number
) {
  let currentX,
    currentT,
    i = 0;
  do {
    currentT = aA + (aB - aA) / 2.0;
    currentX = calcBezier(currentT, mX1, mX2) - aX;
    if (currentX > 0.0) {
      aB = currentT;
    } else {
      aA = currentT;
    }
  } while (
    Math.abs(currentX) > SUBDIVISION_PRECISION &&
    ++i < SUBDIVISION_MAX_ITERATIONS
  );
  return currentT;
}

function newtonRaphsonIterate(
  aX: number,
  aGuessT: number,
  mX1: number,
  mX2: number
) {
  for (let i = 0; i < NEWTON_ITERATIONS; ++i) {
    const currentSlope = getSlope(aGuessT, mX1, mX2);
    if (currentSlope === 0.0) {
      return aGuessT;
    }
    const currentX = calcBezier(aGuessT, mX1, mX2) - aX;
    aGuessT -= currentX / currentSlope;
  }
  return aGuessT;
}

function LinearEasing(x: number) {
  return x;
}

// const easing = BezierEasing(0, 0, 1, 0.5);
// // easing allows to project x in [0.0,1.0] range onto the bezier-curve defined by the 4 points (see schema below).
// console.log(easing(0.0)); // 0.0
// console.log(easing(0.5)); // 0.3125
// console.log(easing(1.0)); // 1.0
export function BezierEasing(
  mX1: number,
  mY1: number,
  mX2: number,
  mY2: number
): (x: number) => number {
  if (!(0 <= mX1 && mX1 <= 1 && 0 <= mX2 && mX2 <= 1)) {
    throw new Error("bezier x values must be in [0, 1] range");
  }

  if (mX1 === mY1 && mX2 === mY2) {
    return LinearEasing;
  }

  // Precompute samples table
  const sampleValues = float32ArraySupported
    ? new Float32Array(kSplineTableSize)
    : new Array(kSplineTableSize);
  for (let i = 0; i < kSplineTableSize; ++i) {
    sampleValues[i] = calcBezier(i * kSampleStepSize, mX1, mX2);
  }

  function getTForX(aX: number) {
    let intervalStart = 0.0;
    let currentSample = 1;
    const lastSample = kSplineTableSize - 1;

    for (
      ;
      currentSample !== lastSample && sampleValues[currentSample] <= aX;
      ++currentSample
    ) {
      intervalStart += kSampleStepSize;
    }
    --currentSample;

    // Interpolate to provide an initial guess for t
    const dist =
      (aX - sampleValues[currentSample]) /
      (sampleValues[currentSample + 1] - sampleValues[currentSample]);
    const guessForT = intervalStart + dist * kSampleStepSize;

    const initialSlope = getSlope(guessForT, mX1, mX2);
    if (initialSlope >= NEWTON_MIN_SLOPE) {
      return newtonRaphsonIterate(aX, guessForT, mX1, mX2);
    } else if (initialSlope === 0.0) {
      return guessForT;
    } else {
      return binarySubdivide(
        aX,
        intervalStart,
        intervalStart + kSampleStepSize,
        mX1,
        mX2
      );
    }
  }

  return (x: number) => {
    // Because JavaScript number are imprecise, we should guarantee the extremes are right.
    if (x === 0 || x === 1) {
      return x;
    }
    return calcBezier(getTForX(x), mY1, mY2);
  };
}

export type SupportedEaseNameToCubicBezier =
  | "linear"
  | "ease"
  | "ease-in"
  | "ease-in-out"
  | "ease-out";

export function easeNameToCubicBezier(
  easeName: SupportedEaseNameToCubicBezier
): [number, number, number, number] {
  switch (easeName) {
    case "linear":
      // cubic-bezier(0, 0, 1, 1)
      return [0, 0, 1, 1];
    case "ease":
      // cubic-bezier(0.25, 0.1, 0.25, 1)
      return [0.25, 0.1, 0.25, 1];
    case "ease-in":
      // cubic-bezier(0.42, 0, 1, 1)
      return [0.42, 0, 1, 1];
    case "ease-in-out":
      // cubic-bezier(0.42, 0, 0.58, 1)
      return [0.42, 0, 0.58, 1];
    case "ease-out":
      // cubic-bezier(0, 0, 0.58, 1)
      return [0, 0, 0.58, 1];
  }
}

export function reverseEaseNameToCubicBezier(
  easeName: SupportedEaseNameToCubicBezier
): [number, number, number, number] {
  const [x1, y1, x2, y2] = easeNameToCubicBezier(easeName);
  return [1 - x2, 1 - y2, 1 - x1, 1 - y1];
}

export function CSSToJSEasing(
  easeName: SupportedEaseNameToCubicBezier
): EasingFunction {
  return BezierEasing(...easeNameToCubicBezier(easeName));
}

export function reverseCSSToJSEasing(
  easeName: SupportedEaseNameToCubicBezier
): EasingFunction {
  return BezierEasing(...reverseEaseNameToCubicBezier(easeName));
}
